import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Swal from "sweetalert2";
import API from "../api/api";

const hasExportManagerAccess = () => {
  try {
    const storedRole = window.sessionStorage.getItem("role");
    const storedUser = JSON.parse(window.sessionStorage.getItem("user") || "null");
    const role = String(storedRole || storedUser?.role || "").toLowerCase();
    return role === "admin" || role === "secretary";
  } catch (_e) {
    return false;
  }
};

/**
 * Wait for all images within a container to finish loading.
 */
async function waitForImages(container) {
  const images = Array.from(container.querySelectorAll("img"));
  if (images.length === 0) return;

  await Promise.race([
    Promise.all(
      images.map((img) => {
        if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // don't block on broken images
        });
      })
    ),
    new Promise((resolve) => setTimeout(resolve, 3000)), // max 3s timeout
  ]);
}

/**
 * Triggers browser download of a given URL
 */
export function triggerBrowserFileDownload(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    if (link.parentNode) link.parentNode.removeChild(link);
  }, 1000);
}

/**
 * Triggers browser auto-download with strict sessionStorage deduplication.
 * Guarantees that a completed job will never trigger more than once per browser session,
 * regardless of component re-renders, route navigation, or multiple event listeners.
 */
export function triggerJobAutoDownload(jobId, downloadUrl, filename) {
  if (!jobId || !downloadUrl) return false;
  const storageKey = "parasports_auto_downloaded_jobs";
  let downloadedIds = [];
  try {
    downloadedIds = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
  } catch (_e) {
    downloadedIds = [];
  }

  const strId = String(jobId);
  if (downloadedIds.includes(strId)) {
    return false; // Already auto-downloaded, skip duplicate download
  }

  downloadedIds.push(strId);
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(downloadedIds));
  } catch (_e) {}

  triggerBrowserFileDownload(downloadUrl, filename);
  return true;
}

/**
 * Tracks a server-side asynchronous PDF generation job with live progress modal,
 * real-time statistics, and automatic background queueing.
 */
export async function trackServerPdfExport(jobId, filename = "Report", endpointOverrides = {}) {
  const usesDownloadManager = hasExportManagerAccess();
  let timer = null;
  let completed = false;
  let userDismissed = false;
  const startTime = Date.now();
  const cleanFilename = `${filename.replace(/\.pdf$/i, "").replace(/[^a-zA-Z0-9_\- ]/g, "_")}.pdf`;

  const stopPolling = () => {
    if (timer) { clearInterval(timer); timer = null; }
  };

  const buildHtml = (progress, statusText, totalPages) => {
    const percent = Math.min(100, Math.max(0, Math.round(progress || 0)));
    const elapsed = Math.round((Date.now() - startTime) / 1000);
    return `
      <div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; text-align: left; padding: 6px 8px;">
        <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px;">
          <span style="font-size: 13px; font-weight: 700; color: #0f172a;">${statusText || "Processing PDF on server..."}</span>
          <span style="font-size: 14px; font-weight: 900; color: #0284c7; font-family: monospace;">${percent}%</span>
        </div>
        <div style="background: #e2e8f0; border-radius: 9999px; height: 10px; width: 100%; overflow: hidden; box-shadow: inset 0 1px 2px rgba(0,0,0,0.08);">
          <div style="background: linear-gradient(90deg, #0284c7 0%, #06b6d4 100%); width: ${percent}%; height: 100%; border-radius: 9999px; transition: width 0.3s ease-in-out;"></div>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 11px; color: #64748b; margin-top: 8px; font-weight: 600;">
          <span>${totalPages ? `${totalPages} Vector Pages` : "High-Definition Vector PDF"}</span>
          <span>${elapsed}s elapsed &bull; Server Worker</span>
        </div>
        <div style="margin-top: 10px; font-size: 11px; color: #64748b; background: #f1f5f9; border-radius: 8px; padding: 8px 10px;">
          ${usesDownloadManager
            ? "You can dismiss this popup and use the Downloads Manager while the file is generated."
            : "Please wait. Your file will download automatically as soon as it is ready."}
        </div>
      </div>
    `;
  };

  // Show initial modal — fully dismissable via outside click, Esc, or "Send to Background"
  Swal.fire({
    title: "Generating High-Speed PDF...",
    html: buildHtml(10, "Job dispatched to server background queue...", 0),
    allowOutsideClick: usesDownloadManager,
    allowEscapeKey: usesDownloadManager,
    showConfirmButton: usesDownloadManager,
    confirmButtonText: usesDownloadManager ? "Send to Background" : undefined,
    confirmButtonColor: "#475569",
    showCancelButton: false,
  }).then(() => {
    userDismissed = true;
  });

  const pollInterval = 1200;
  let attempts = 0;
  let noResponseStreak = 0;
  const maxAttempts = 200;
  const maxNoResponse = 10;

  const checkStatus = async () => {
    if (completed) { stopPolling(); return; }

    attempts++;
    try {
      const res = endpointOverrides.getStatus
        ? await endpointOverrides.getStatus(jobId)
        : await API.camp.getExportStatus(jobId);
      const job = res?.job || res?.data?.job;
      noResponseStreak = 0;

      if (!job) return;

      if (usesDownloadManager) {
        window.dispatchEvent(new CustomEvent("camp-export-job-updated", { detail: job }));
      }

      if (job.status === "completed") {
        completed = true;
        stopPolling();
        if (usesDownloadManager) {
          window.dispatchEvent(new CustomEvent("camp-export-job-completed", { detail: job }));
        }

        if (Swal.isVisible()) {
          Swal.close();
        }

        // Auto-download with session deduplication
        const downloadUrl = endpointOverrides.getDownloadUrl
          ? endpointOverrides.getDownloadUrl(job.id)
          : (job.download_url || API.camp.getExportDownloadUrl(job.id));
        // Respect the file actually generated by the server. Large exports
        // can be ZIP bundles; forcing a .pdf name makes an otherwise valid
        // archive appear corrupt or produces an invalid PDF download.
        const serverFilename = job.file_name || job.filename || cleanFilename;
        triggerJobAutoDownload(job.id, downloadUrl, serverFilename);
        return;
      }

      if (job.status === "failed") {
        completed = true;
        stopPolling();
        if (usesDownloadManager) {
          window.dispatchEvent(new CustomEvent("camp-export-job-failed", { detail: job }));
        }
        if (!userDismissed && Swal.isVisible()) {
          Swal.fire({
            icon: "error",
            title: "PDF Generation Failed",
            text: job.error_message || "Server encountered an error while compiling the PDF document.",
          });
        }
        return;
      }

      if (job.status === "cancelled") {
        completed = true;
        stopPolling();
        if (Swal.isVisible()) {
          Swal.close();
        }
        return;
      }

      // Update modal ONLY if still open and user hasn't dismissed it
      if (!userDismissed && Swal.isVisible()) {
        Swal.update({
          title: "Generating High-Speed PDF...",
          html: buildHtml(job.progress, job.current_step, job.total_pages),
          showConfirmButton: usesDownloadManager,
          confirmButtonText: usesDownloadManager ? "Send to Background" : undefined,
          confirmButtonColor: "#475569",
        });
      }

      if (attempts >= maxAttempts) {
        completed = true;
        stopPolling();
        if (Swal.isVisible()) {
          Swal.close();
        }
      }
    } catch (e) {
      noResponseStreak++;
      console.warn(`Poll error for export job (attempt ${noResponseStreak}):`, e);

      if (noResponseStreak >= maxNoResponse && !completed && !userDismissed && Swal.isVisible()) {
        Swal.update({
          title: "Server Taking Longer...",
          html: `<div style="font-size:13px; color:#334155;">The server is taking longer than expected. ${usesDownloadManager ? "You may close this message and use the Downloads Manager." : "Please keep this page open; the file will download automatically when ready."}</div>`,
          showConfirmButton: usesDownloadManager,
          confirmButtonText: usesDownloadManager ? "Close & Continue Working" : undefined,
          confirmButtonColor: "#475569",
        });
      }
    }
  };

  timer = setInterval(checkStatus, pollInterval);
}

/**
 * Downloads a complete HTML string directly as a PDF.
 * Uses the fast server-side asynchronous Chromium queue by default,
 * with automatic fallback to client-side rendering if server is unreachable.
 *
 * @param {string} fullHtml - The full HTML document string
 * @param {string} filename - The output PDF filename (without extension)
 * @param {object} options - Options: orientation ('landscape'|'portrait'), scale, etc.
 */
export async function downloadHtmlAsPdf(fullHtml, filename = "Report", options = {}) {
  const isLandscape =
    options.orientation === "landscape" ||
    (!options.orientation && (fullHtml.includes("landscape") || fullHtml.includes("size: A4 landscape")));

  const cleanFilename = `${filename.replace(/\.pdf$/i, "").replace(/[^a-zA-Z0-9_\- ]/g, "_")}`;

  // 1. Attempt high-speed server background export
  let serverFailure = null;
  try {
    const jobRes = await API.camp.createExportJob({
      export_type: "raw_html",
      title: cleanFilename,
      html: fullHtml,
      params: {
        filename: cleanFilename,
        orientation: isLandscape ? "landscape" : "portrait",
      },
    });

    const job = jobRes?.job || jobRes?.data?.job;
    if (job && job.id) {
      if (hasExportManagerAccess()) {
        window.dispatchEvent(new CustomEvent("camp-export-job-started", { detail: job }));
      }
      trackServerPdfExport(job.id, cleanFilename);
      return;
    }
  } catch (serverErr) {
    serverFailure = serverErr;
    console.warn("Server-side PDF generation failed or timed out:", serverErr);
  }

  // 2. Safeguard: ONLY run client-side canvas fallback for small documents (<= 3 pages, < 300KB)
  // Running html2canvas on large batches locks the single-threaded JS engine and makes Chrome unresponsive.
  const estimatedPages = (fullHtml.match(/class=["'][^"']*print-(?:page|sheet|report)[^"']*["']/gi) || []).length;
  const isLargeDocument = fullHtml.length > 300000 || estimatedPages > 3;

  if (isLargeDocument) {
    Swal.fire({
      icon: "error",
      title: "Server Export Queue Unavailable",
      html: `
        <div style="font-size: 13px; color: #334155; text-align: left; line-height: 1.6;">
          <p style="margin-bottom: 8px;">The background PDF export worker could not process this large batch request (${serverFailure?.response?.data?.message || serverFailure?.message || "Server Error / Timeout"}).</p>
          <p style="margin-bottom: 0; font-size: 11.5px; color: #64748b;">To prevent Chrome from becoming unresponsive or crashing your tab, client-side rendering was safely skipped. Please ensure the backend server is running and try again.</p>
        </div>
      `,
    });
    return;
  }

  // 3. Fallback only for small individual single/few page documents
  await fallbackClientSidePdf(fullHtml, cleanFilename, isLandscape, options);
}

/**
 * Prints an HTML string directly in the browser using an isolated hidden iframe.
 * Ideal for direct paper printing dialogs without downloading files.
 *
 * @param {string} fullHtml - The full HTML document string
 */
export function printHtmlViaIframe(fullHtml) {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.zIndex = "-9999";
  document.body.appendChild(iframe);

  let enhancedHtml = fullHtml;
  const baseTag = `<base href="${window.location.origin}/" />`;
  if (!enhancedHtml.includes("<base ")) {
    if (enhancedHtml.includes("<head>")) {
      enhancedHtml = enhancedHtml.replace("<head>", `<head>\n  ${baseTag}`);
    } else if (enhancedHtml.includes("<head ")) {
      enhancedHtml = enhancedHtml.replace(/<head[^>]*>/, `$& \n  ${baseTag}`);
    }
  }

  // Clone document stylesheets so all Tailwind classes work in the isolated iframe
  let headStyles = "";
  try {
    const styleTags = document.querySelectorAll("style, link[rel='stylesheet']");
    styleTags.forEach((tag) => {
      headStyles += tag.outerHTML + "\n";
    });
  } catch (_e) {}

  if (headStyles && enhancedHtml.includes("</head>")) {
    enhancedHtml = enhancedHtml.replace("</head>", `${headStyles}\n</head>`);
  }

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(enhancedHtml);
  doc.close();

  const triggerPrint = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      console.error("Iframe print failed:", e);
    }
    setTimeout(() => {
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 2500);
  };

  const images = Array.from(doc.images || []);
  const imagePromises = images.map((img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    if (typeof img.decode === "function") {
      return img.decode().catch(() => undefined);
    }
    return new Promise((resolve) => {
      img.addEventListener("load", resolve, { once: true });
      img.addEventListener("error", resolve, { once: true });
    });
  });
  // Official report logos and athlete photos are CSS background images.
  // They do not appear in doc.images, so explicitly preload every CSS URL
  // before opening the browser print dialog.
  const backgroundUrls = Array.from(new Set(
    Array.from(enhancedHtml.matchAll(/url\(\s*["']?([^"')]+)["']?\s*\)/gi))
      .map((match) => match[1])
      .filter((url) => url && !url.startsWith("data:"))
  ));
  backgroundUrls.forEach((url) => {
    imagePromises.push(new Promise((resolve) => {
      const preload = new Image();
      preload.onload = resolve;
      preload.onerror = resolve;
      preload.src = url;
    }));
  });
  const fontsPromise = doc.fonts?.ready ? doc.fonts.ready.catch(() => undefined) : Promise.resolve();
  const assetsReady = Promise.allSettled([...imagePromises, fontsPromise]);
  const timeout = new Promise((resolve) => setTimeout(resolve, 10000));

  Promise.race([assetsReady, timeout]).then(() => setTimeout(triggerPrint, 350));
}

/**
 * Captures pages from an in-DOM container and downloads as high-res PDF.
 * Converts DOM container + styles into HTML and delegates to the high-speed server pipeline.
 *
 * @param {string|HTMLElement} containerOrSelector - Container selector or element
 * @param {string} filename - Output filename
 * @param {object} options - Options: orientation, pageSelector, scale
 */
export async function downloadDomPagesAsPdf(containerOrSelector, filename = "Report", options = {}) {
  const container =
    typeof containerOrSelector === "string"
      ? document.querySelector(containerOrSelector)
      : containerOrSelector;

  if (!container) {
    Swal.fire("Notice", "No printable content found to generate PDF.", "warning");
    return;
  }

  // Collect all stylesheet rules so the server can render full styles accurately
  let cssText = "";
  try {
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules || [])) {
          cssText += rule.cssText + "\n";
        }
      } catch (_e) {
        // Cross-origin stylesheet rules can be ignored
      }
    }
  } catch (_se) {}

  // Strip any screen-hiding rules that might suppress .print-only-container
  cssText = cssText.replace(/@media\s+screen\s*\{[^}]*\.print-only-container\s*\{[^}]*display\s*:\s*none[^}]*\}[^}]*\}/gi, "");

  // Strip external web font imports & @font-face rules so server-side Dompdf never attempts remote font downloads
  cssText = cssText.replace(/@import\s+url\([^)]+\)\s*;?/gi, "");
  cssText = cssText.replace(/@font-face\s*\{[^}]*\}/gi, "");
  cssText = cssText.replace(/font-family\s*:\s*[^;]*(Inter|Playfair)[^;]*/gi, "font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif");

  // Use innerHTML so each report page is a direct child of body for pristine pagination without wrapper clipping
  const pagesHtml = container.innerHTML;

  const fullHtml = getUnifiedPrintHtml(filename, pagesHtml, options, cssText);

  return downloadHtmlAsPdf(fullHtml, filename, options);
}

/**
 * Returns comprehensive unified CSS rules for all camp reports, evaluation cards, and registers.
 * Guarantees that browser print preview and server/client PDF downloads look 100% identical.
 */
export function getUnifiedCampReportStyles(options = {}) {
  return `
    @page {
      size: A4 ${options?.orientation === "landscape" ? "landscape" : "portrait"};
      margin: ${options?.orientation === "landscape" ? "8mm 10mm" : "5mm 8mm"};
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      box-sizing: border-box !important;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #0f172a;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    .print-only-container, [class*="print-only"], .print-container {
      display: block !important;
      visibility: visible !important;
      width: 100% !important;
      opacity: 1 !important;
    }
    .print-report-page {
      page-break-after: always !important;
      break-after: page !important;
      box-sizing: border-box !important;
      background: #ffffff !important;
      color: #0f172a !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 3mm 5mm !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      max-height: 273mm !important;
      height: 100% !important;
      overflow: hidden !important;
    }
    .print-report-page:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
    .individual-student-page {
      page-break-after: always !important;
      break-after: page !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      box-sizing: border-box !important;
      margin: 0 !important;
      padding: 3mm 5mm 2mm 5mm !important;
      height: 270mm !important;
      max-height: 270mm !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      background: #ffffff !important;
    }
    .individual-student-page:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
    .individual-student-page > div:last-child {
      page-break-before: avoid !important;
      break-before: avoid !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .print-sheet, .team-sheet {
      page-break-after: always !important;
      break-after: page !important;
      box-sizing: border-box !important;
      background: #ffffff !important;
      margin: 0 !important;
      padding: 2mm 3mm !important;
      height: 270mm !important;
      max-height: 270mm !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      overflow: hidden !important;
    }
    .print-sheet:last-child, .team-sheet:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
    table {
      width: 100% !important;
      border-collapse: collapse !important;
    }
    /* Layout & Utilities */
    .grid { display: grid !important; }
    .grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)) !important; }
    .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
    .grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
    .grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; }
    .grid-cols-5 { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; }
    .grid-cols-6 { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; }
    .flex { display: flex !important; }
    .flex-col { flex-direction: column !important; }
    .flex-row { flex-direction: row !important; }
    .flex-1 { flex: 1 1 0% !important; }
    .shrink-0 { flex-shrink: 0 !important; }
    .items-center { align-items: center !important; }
    .items-start { align-items: flex-start !important; }
    .items-end { align-items: flex-end !important; }
    .justify-between { justify-content: space-between !important; }
    .justify-center { justify-content: center !important; }
    .justify-start { justify-content: flex-start !important; }
    .justify-end { justify-content: flex-end !important; }
    .gap-1 { gap: 0.25rem !important; }
    .gap-1\\.5 { gap: 0.375rem !important; }
    .gap-2 { gap: 0.5rem !important; }
    .gap-2\\.5 { gap: 0.625rem !important; }
    .gap-3 { gap: 0.75rem !important; }
    .gap-4 { gap: 1rem !important; }
    .gap-x-3 { column-gap: 0.75rem !important; }
    .gap-x-4 { column-gap: 1rem !important; }
    .gap-y-1 { row-gap: 0.25rem !important; }
    .gap-y-1\\.5 { row-gap: 0.375rem !important; }
    .gap-y-2 { row-gap: 0.5rem !important; }
    .space-y-1 > * + * { margin-top: 0.25rem !important; }
    .space-y-2 > * + * { margin-top: 0.5rem !important; }
    .space-y-2\\.5 > * + * { margin-top: 0.625rem !important; }
    .space-y-3 > * + * { margin-top: 0.75rem !important; }
    /* Borders */
    .border { border: 1px solid #0f172a !important; }
    .border-2 { border: 2px solid #0f172a !important; }
    .border-b { border-bottom: 1px solid #0f172a !important; }
    .border-b-2 { border-bottom: 2px solid #0f172a !important; }
    .border-t { border-top: 1px solid #0f172a !important; }
    .border-t-2 { border-top: 2px solid #0f172a !important; }
    .border-dashed { border-bottom-style: dashed !important; }
    .border-slate-200 { border-color: #e2e8f0 !important; }
    .border-slate-300 { border-color: #cbd5e1 !important; }
    .border-slate-400 { border-color: #94a3b8 !important; }
    .border-slate-800 { border-color: #1e293b !important; }
    .border-slate-900 { border-color: #0f172a !important; }
    .border-slate-950 { border-color: #020617 !important; }
    .rounded { border-radius: 4px !important; }
    .rounded-sm { border-radius: 2px !important; }
    .rounded-md { border-radius: 6px !important; }
    /* Padding & Margin */
    .p-0\\.5 { padding: 0.125rem !important; }
    .p-1 { padding: 0.25rem !important; }
    .p-1\\.5 { padding: 0.375rem !important; }
    .p-2 { padding: 0.5rem !important; }
    .p-2\\.5 { padding: 0.625rem !important; }
    .p-3 { padding: 0.75rem !important; }
    .p-4 { padding: 1rem !important; }
    .px-2 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
    .px-3 { padding-left: 0.75rem !important; padding-right: 0.75rem !important; }
    .px-4 { padding-left: 1rem !important; padding-right: 1rem !important; }
    .py-0\\.5 { padding-top: 0.125rem !important; padding-bottom: 0.125rem !important; }
    .py-1 { padding-top: 0.25rem !important; padding-bottom: 0.25rem !important; }
    .py-1\\.5 { padding-top: 0.375rem !important; padding-bottom: 0.375rem !important; }
    .py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
    .pb-1 { margin-bottom: 0.25rem !important; }
    .pb-1\\.5 { padding-bottom: 0.375rem !important; }
    .pb-2 { padding-bottom: 0.5rem !important; }
    .pb-3 { padding-bottom: 0.75rem !important; }
    .pt-0\\.5 { padding-top: 0.125rem !important; }
    .pt-1 { padding-top: 0.25rem !important; }
    .pt-1\\.5 { padding-top: 0.375rem !important; }
    .pt-2 { padding-top: 0.5rem !important; }
    .pt-3 { padding-top: 0.75rem !important; }
    .pt-4 { padding-top: 1rem !important; }
    .mb-0\\.5 { margin-bottom: 0.125rem !important; }
    .mb-1 { margin-bottom: 0.25rem !important; }
    .mb-1\\.5 { margin-bottom: 0.375rem !important; }
    .mb-2 { margin-bottom: 0.5rem !important; }
    .mb-2\\.5 { margin-bottom: 0.625rem !important; }
    .mb-3 { margin-bottom: 0.75rem !important; }
    .mt-0\\.5 { margin-top: 0.125rem !important; }
    .mt-1 { margin-top: 0.25rem !important; }
    .mt-1\\.5 { margin-top: 0.375rem !important; }
    .mt-2 { margin-top: 0.5rem !important; }
    .mt-auto { margin-top: auto !important; }
    /* Typography & Colors */
    .text-center { text-align: center !important; }
    .text-left { text-align: left !important; }
    .text-right { text-align: right !important; }
    .uppercase { text-transform: uppercase !important; }
    .font-mono { font-family: monospace !important; }
    .font-medium { font-weight: 500 !important; }
    .font-semibold { font-weight: 600 !important; }
    .font-bold { font-weight: 700 !important; }
    .font-extrabold { font-weight: 800 !important; }
    .font-black { font-weight: 900 !important; }
    .text-xs { font-size: 0.75rem !important; line-height: 1rem !important; }
    .text-sm { font-size: 0.875rem !important; line-height: 1.25rem !important; }
    .text-base { font-size: 1rem !important; line-height: 1.5rem !important; }
    .text-lg { font-size: 1.125rem !important; line-height: 1.75rem !important; }
    .text-\\[8px\\] { font-size: 8px !important; }
    .text-\\[8\\.5px\\] { font-size: 8.5px !important; }
    .text-\\[9px\\] { font-size: 9px !important; }
    .text-\\[9\\.5px\\] { font-size: 9.5px !important; }
    .text-\\[10px\\] { font-size: 10px !important; }
    .text-\\[10\\.5px\\] { font-size: 10.5px !important; }
    .text-\\[11px\\] { font-size: 11px !important; }
    .text-\\[11\\.5px\\] { font-size: 11.5px !important; }
    .text-\\[12px\\] { font-size: 12px !important; }
    .text-\\[12\\.5px\\] { font-size: 12.5px !important; }
    .text-\\[13px\\] { font-size: 13px !important; }
    .text-\\[13\\.5px\\] { font-size: 13.5px !important; }
    .text-\\[14px\\] { font-size: 14px !important; }
    .tracking-tight { letter-spacing: -0.025em !important; }
    .tracking-wide { letter-spacing: 0.025em !important; }
    .tracking-wider { letter-spacing: 0.05em !important; }
    .leading-none { line-height: 1 !important; }
    .leading-tight { line-height: 1.25 !important; }
    .leading-snug { line-height: 1.375 !important; }
    .leading-normal { line-height: 1.5 !important; }
    .text-slate-950 { color: #020617 !important; }
    .text-slate-900 { color: #0f172a !important; }
    .text-slate-800 { color: #1e293b !important; }
    .text-slate-700 { color: #334155 !important; }
    .text-slate-600 { color: #475569 !important; }
    .text-slate-500 { color: #64748b !important; }
    .text-slate-400 { color: #94a3b8 !important; }
    .text-cyan-800 { color: #155e75 !important; }
    .text-cyan-900 { color: #164e63 !important; }
    .text-sky-700 { color: #0369a1 !important; }
    .text-sky-800 { color: #075985 !important; }
    .text-white { color: #ffffff !important; }
    .bg-white { background-color: #ffffff !important; }
    .bg-slate-50 { background-color: #f8fafc !important; }
    .bg-slate-100 { background-color: #f1f5f9 !important; }
    .bg-slate-200 { background-color: #e2e8f0 !important; }
    .bg-slate-800 { background-color: #1e293b !important; }
    .bg-slate-900 { background-color: #0f172a !important; }
    .bg-slate-950 { background-color: #020617 !important; }
    /* Sizing & Images */
    .w-16 { width: 4rem !important; }
    .w-20 { width: 5rem !important; }
    .w-24 { width: 6rem !important; }
    .h-4 { height: 1rem !important; }
    .h-4\\.5 { height: 1.125rem !important; }
    .h-10 { height: 2.5rem !important; }
    .h-14 { height: 3.5rem !important; }
    .h-16 { height: 4rem !important; }
    .h-20 { height: 5rem !important; }
    .h-24 { height: 6rem !important; }
    .w-full { width: 100% !important; }
    .h-full { height: 100% !important; }
    .object-cover { object-fit: cover !important; }
    .object-contain { object-fit: contain !important; }
    .inline-block { display: inline-block !important; }
    .overflow-hidden { overflow: hidden !important; }
    .hidden { display: none !important; }
    img { max-width: 100%; }
    .report-info-table { display: table !important; width: 100% !important; }
    .report-info-row { display: table-row !important; }
    .report-info-col { display: table-cell !important; width: 50% !important; padding: 2px 4px !important; vertical-align: top !important; }
    .report-sig-row { display: table !important; width: 100% !important; margin-top: 6px !important; }
    .report-sig-cell { display: table-cell !important; width: 33.333% !important; text-align: center !important; vertical-align: top !important; padding: 0 8px !important; }
  `;
}

/**
 * Builds a complete self-contained HTML document with unified styles and base URL.
 */
export function getUnifiedPrintHtml(title, bodyContent, options = {}, extraCss = "") {
  const unifiedStyles = getUnifiedCampReportStyles(options);
  const baseTag = typeof window !== "undefined" ? `<base href="${window.location.origin}/" />` : "";
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  ${baseTag}
  <title>${title || "Camp Report"}</title>
  <style>
    ${unifiedStyles}
    ${extraCss}
  </style>
</head>
<body>
  ${bodyContent}
</body>
</html>`;
}

/**
 * Client-side fallback using jsPDF + html2canvas
 */
async function fallbackClientSidePdf(fullHtml, filename, isLandscape, options) {
  const orientation = isLandscape ? "landscape" : "portrait";
  const a4WidthMm = isLandscape ? 297 : 210;
  const a4HeightMm = isLandscape ? 210 : 297;
  const containerWidthPx = isLandscape ? 1123 : 794;

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.left = "-99999px";
  iframe.style.top = "0";
  iframe.style.width = `${containerWidthPx}px`;
  iframe.style.height = "1600px";
  iframe.style.border = "none";
  iframe.style.zIndex = "-9999";
  document.body.appendChild(iframe);

  try {
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(fullHtml);
    iframeDoc.close();

    if (iframeDoc.fonts && iframeDoc.fonts.ready) {
      await Promise.race([iframeDoc.fonts.ready, new Promise((r) => setTimeout(r, 1000))]);
    }
    await waitForImages(iframeDoc.body);
    await new Promise((r) => setTimeout(r, 200));

    const pageSelector =
      options.pageSelector ||
      ".team-sheet, .print-page, .report-page, .register-sheet, .individual-student-page, .print-report-page, .print-sheet";
    let pages = Array.from(iframeDoc.querySelectorAll(pageSelector));

    if (pages.length === 0) {
      const bodyChildren = Array.from(iframeDoc.body.children).filter(
        (el) => el.tagName !== "SCRIPT" && el.tagName !== "STYLE"
      );
      pages = bodyChildren.length > 0 ? bodyChildren : [iframeDoc.body];
    }

    const pdf = new jsPDF({
      orientation,
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const totalPages = pages.length;
    if (totalPages > 50) {
      Swal.fire({
        icon: "warning",
        title: "Export Too Large for Browser",
        text: `This document has ${totalPages} pages. In-browser rendering is limited to 50 pages at a time to prevent browser memory issues. Please filter by team or single athlete.`,
      });
      return;
    }

    for (let i = 0; i < totalPages; i++) {
      if (i > 0) pdf.addPage();
      const page = pages[i];

      const canvas = await html2canvas(page, {
        scale: options.scale || 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: containerWidthPx,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.98);
      pdf.addImage(imgData, "JPEG", 0, 0, a4WidthMm, a4HeightMm, undefined, "FAST");
    }

    pdf.save(`${filename}.pdf`);
    Swal.close();
  } catch (err) {
    console.error("Client fallback PDF generation failed:", err);
    Swal.fire({
      icon: "error",
      title: "PDF Generation Failed",
      text: "Could not generate PDF. Please try again.",
    });
  } finally {
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }
}
